import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Users, DollarSign, Calendar as CalendarIcon, Clock, LogOut, Mail, Phone, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import AdminCalendar from './AdminCalendar';

const API = window.location.origin + '/api';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [loading, setLoading] = useState(!user);
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [bookingFilter, setBookingFilter] = useState('all');

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  const checkAuth = async () => {
    if (user) return;
    
    try {
      const response = await axios.get(`${API}/auth/me`, { 
        headers: getAuthHeaders(),
        withCredentials: true 
      });
      setUser(response.data);
      setLoading(false);
    } catch (error) {
      localStorage.removeItem('admin_token');
      navigate('/admin');
    }
  };

  const fetchData = async () => {
    try {
      const headers = getAuthHeaders();
      const opts = { headers, withCredentials: true };
      const [statsRes, bookingsRes, enquiriesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, opts),
        axios.get(`${API}/admin/bookings`, opts),
        axios.get(`${API}/admin/enquiries`, opts)
      ]);
      
      setStats(statsRes.data);
      setBookings(bookingsRes.data);
      setEnquiries(enquiriesRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin');
        return;
      }
      toast.error('Error loading dashboard data');
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { headers: getAuthHeaders(), withCredentials: true });
    } catch (error) {
      // ignore
    }
    localStorage.removeItem('admin_token');
    navigate('/admin');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-16 h-16 animate-spin text-primary" />
      </div>
    );
  }

  const filteredBookings = bookingFilter === 'all'
    ? bookings
    : bookings.filter(b => b.session_type === bookingFilter);

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-gradient-to-br from-muted/30 to-background">
      {/* Header */}
      <div className="glass-nav sticky top-0 z-50 border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-accent flex items-center justify-center">
              <svg className="w-7 h-7 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 10v6M2 10l10-5 10 5-10 5z"></path>
                <path d="M6 12v5c3 3 9 3 12 0v-5"></path>
              </svg>
            </div>
            <div>
              <div className="flex flex-col leading-tight">
                <h1 className="text-xl font-bold" style={{fontFamily: 'Fraunces, serif'}}>Igniting Minds</h1>
                <span className="text-xs font-medium text-muted-foreground tracking-wide">Academic Architect</span>
              </div>
              <p className="text-xs text-muted-foreground">Admin Dashboard</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <Button 
              data-testid="fix-db-btn" 
              variant="outline" 
              size="sm" 
              className="gap-2 text-orange-600 border-orange-200 hover:bg-orange-50"
              onClick={async () => {
                try {
                  const res = await axios.post(`${API}/admin/fix-database`, {}, { headers: getAuthHeaders(), withCredentials: true });
                  toast.success(`Database fixed: ${res.data.message}`);
                  window.location.reload();
                } catch (e) {
                  toast.error('Fix failed: ' + (e.response?.data?.detail || e.message));
                }
              }}
            >
              Fix DB
            </Button>
            <Button 
              data-testid="clean-db-btn" 
              variant="outline" 
              size="sm" 
              className="gap-2 text-red-600 border-red-200 hover:bg-red-50"
              onClick={async () => {
                if (!window.confirm('This will DELETE all slots, bookings, and transactions. Are you sure?')) return;
                try {
                  const res = await axios.post(`${API}/admin/clean-database`, {}, { headers: getAuthHeaders(), withCredentials: true });
                  toast.success(`Cleaned: ${JSON.stringify(res.data.deleted)}`);
                  window.location.reload();
                } catch (e) {
                  toast.error('Clean failed: ' + (e.response?.data?.detail || e.message));
                }
              }}
            >
              Clean DB
            </Button>
            <div className="hidden md:flex items-center gap-3 px-4 py-2 rounded-xl bg-muted/50">
              <img 
                src={user?.picture || 'https://via.placeholder.com/40'} 
                alt={user?.name}
                className="w-9 h-9 rounded-full border-2 border-primary/20"
              />
              <div className="text-sm">
                <p className="font-semibold">{user?.name}</p>
                <p className="text-muted-foreground text-xs">{user?.email}</p>
              </div>
            </div>
            <Button data-testid="logout-btn" variant="outline" size="sm" onClick={handleLogout} className="gap-2">
              <LogOut className="w-4 h-4" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid md:grid-cols-4 gap-6 mb-8">
            <Card data-testid="stat-slots" className="card-feature bg-gradient-to-br from-purple-50 to-white border-purple-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">Total Slots</p>
                  <p className="text-4xl font-bold text-primary">{stats.total_slots}</p>
                </div>
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
                  <CalendarIcon className="w-7 h-7 text-primary" />
                </div>
              </div>
            </Card>
            
            <Card data-testid="stat-bookings" className="card-feature bg-gradient-to-br from-pink-50 to-white border-pink-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">Bookings</p>
                  <p className="text-4xl font-bold text-secondary">{stats.total_bookings}</p>
                </div>
                <div className="w-14 h-14 rounded-2xl bg-secondary/10 flex items-center justify-center">
                  <Users className="w-7 h-7 text-secondary" />
                </div>
              </div>
            </Card>
            
            <Card data-testid="stat-pending" className="card-feature bg-gradient-to-br from-yellow-50 to-white border-yellow-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">Pending</p>
                  <p className="text-4xl font-bold text-warning">{stats.pending_bookings}</p>
                </div>
                <div className="w-14 h-14 rounded-2xl bg-warning/10 flex items-center justify-center">
                  <Clock className="w-7 h-7 text-warning" />
                </div>
              </div>
            </Card>
            
            <Card data-testid="stat-revenue" className="card-feature bg-gradient-to-br from-teal-50 to-white border-teal-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">Revenue</p>
                  <p className="text-4xl font-bold text-success">${stats.revenue.toFixed(0)}</p>
                </div>
                <div className="w-14 h-14 rounded-2xl bg-success/10 flex items-center justify-center">
                  <DollarSign className="w-7 h-7 text-success" />
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="calendar" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 h-auto p-1 bg-muted/50">
            <TabsTrigger value="calendar" data-testid="tab-calendar" className="data-[state=active]:bg-gradient-accent data-[state=active]:text-white py-3">
              <CalendarIcon className="w-4 h-4 mr-2" />
              Calendar
            </TabsTrigger>
            <TabsTrigger value="bookings" data-testid="tab-bookings" className="data-[state=active]:bg-gradient-accent data-[state=active]:text-white py-3">
              <Users className="w-4 h-4 mr-2" />
              Bookings
            </TabsTrigger>
            <TabsTrigger value="enquiries" data-testid="tab-enquiries" className="data-[state=active]:bg-gradient-accent data-[state=active]:text-white py-3">
              <Mail className="w-4 h-4 mr-2" />
              Enquiries
            </TabsTrigger>
          </TabsList>

          {/* Calendar Tab */}
          <TabsContent value="calendar" data-testid="calendar-tab-content">
            <AdminCalendar />
          </TabsContent>

          {/* Bookings Tab */}
          <TabsContent value="bookings" data-testid="bookings-tab-content">
            <Card className="card-feature p-6">
              <h2 className="text-2xl font-bold mb-6" style={{fontFamily: 'Fraunces, serif'}}>All Bookings</h2>
              
              {/* Type filter buttons */}
              <div className="flex flex-wrap gap-2 mb-6">
                <button
                  data-testid="booking-filter-all"
                  onClick={() => setBookingFilter('all')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                    bookingFilter === 'all' ? 'bg-primary text-white' : 'bg-muted hover:bg-muted/80'
                  }`}
                >All ({bookings.length})</button>
                {[
                  { key: 'one_on_one', label: 'One-on-One', color: 'bg-blue-500', bgLight: 'bg-blue-50', text: 'text-blue-700' },
                  { key: 'paired', label: 'Paired', color: 'bg-purple-500', bgLight: 'bg-purple-50', text: 'text-purple-700' },
                  { key: 'small_group', label: 'Small Group', color: 'bg-teal-500', bgLight: 'bg-teal-50', text: 'text-teal-700' }
                ].map(t => (
                  <button
                    key={t.key}
                    data-testid={`booking-filter-${t.key}`}
                    onClick={() => setBookingFilter(t.key)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      bookingFilter === t.key ? `${t.color} text-white` : `${t.bgLight} ${t.text}`
                    }`}
                  >{t.label}</button>
                ))}
              </div>
              
              <div className="space-y-4">
                {filteredBookings.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Users className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>No bookings yet</p>
                  </div>
                ) : (
                  filteredBookings.map(booking => (
                    <Card key={booking.booking_id} data-testid={`booking-card-${booking.booking_id}`} className="p-6 border-l-4" style={{borderLeftColor: booking.payment_status === 'paid' ? 'hsl(var(--success))' : 'hsl(var(--warning))'}}>
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-3">
                            <h3 className="font-semibold text-lg">{booking.student_name}</h3>
                            <span className={`text-xs px-3 py-1 rounded-full font-medium ${
                              booking.payment_status === 'paid' 
                                ? 'bg-success/10 text-success' 
                                : 'bg-warning/10 text-warning'
                            }`}>
                              {booking.payment_status}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Mail className="w-4 h-4" />
                              {booking.student_email}
                            </div>
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Phone className="w-4 h-4" />
                              {booking.parent_phone}
                            </div>
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <CalendarIcon className="w-4 h-4" />
                              {booking.slot_date} at {booking.slot_time}
                            </div>
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Users className="w-4 h-4" />
                              {booking.num_students} student(s)
                            </div>
                          </div>
                          {booking.subjects_needed && booking.subjects_needed !== 'To be discussed' && (
                            <p className="text-sm text-muted-foreground mt-2">
                              <strong>Subjects:</strong> {booking.subjects_needed}
                            </p>
                          )}
                        </div>
                        
                        <div className="text-right">
                          <p className="text-3xl font-bold text-primary">${booking.total_amount.toFixed(2)}</p>
                          <p className="text-sm text-muted-foreground">AUD</p>
                        </div>
                      </div>
                    </Card>
                  ))
                )}
              </div>
            </Card>
          </TabsContent>

          {/* Enquiries Tab */}
          <TabsContent value="enquiries" data-testid="enquiries-tab-content">
            <Card className="card-feature p-6">
              <h2 className="text-2xl font-bold mb-6" style={{fontFamily: 'Fraunces, serif'}}>Enquiries</h2>
              
              <div className="space-y-4">
                {enquiries.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Mail className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>No enquiries yet</p>
                  </div>
                ) : (
                  enquiries.map(enquiry => (
                    <Card key={enquiry.enquiry_id} data-testid={`enquiry-card-${enquiry.enquiry_id}`} className="p-6 bg-gradient-to-r from-accent/5 to-transparent">
                      <div className="flex flex-col md:flex-row justify-between gap-4">
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg mb-3">{enquiry.name}</h3>
                          <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Mail className="w-4 h-4" />
                              {enquiry.email}
                            </div>
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Phone className="w-4 h-4" />
                              {enquiry.phone}
                            </div>
                          </div>
                          <div className="bg-muted/50 p-4 rounded-xl">
                            <p className="text-sm text-muted-foreground mb-1">Year {enquiry.year_level} student</p>
                            <p className="text-sm">{enquiry.message}</p>
                          </div>
                        </div>
                        
                        <div className="flex md:flex-col gap-2">
                          <a href={`mailto:${enquiry.email}`} className="flex-1">
                            <Button size="sm" className="w-full bg-secondary hover:bg-secondary/90">
                              <Mail className="w-4 h-4 mr-2" />
                              Email
                            </Button>
                          </a>
                          <a href={`tel:${enquiry.phone}`} className="flex-1">
                            <Button size="sm" variant="outline" className="w-full">
                              <Phone className="w-4 h-4 mr-2" />
                              Call
                            </Button>
                          </a>
                        </div>
                      </div>
                    </Card>
                  ))
                )}
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
